import { useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import html2pdf from 'html2pdf.js';
import { Download, Code, Database, Shield, Server, Globe, Layers, Key, Users, Settings, FileText, Zap } from 'lucide-react';

export default function DocumentationReport() {
  const reportContentRef = useRef<HTMLDivElement>(null);

  async function handleExportPDF() {
    if (!reportContentRef.current) {
      alert('Report content not found. Please try again.');
      return;
    }
    
    try {
      // Hide header buttons during export
      const headerElement = document.getElementById('doc-report-header');
      const originalDisplay = headerElement?.style.display || '';
      if (headerElement) {
        headerElement.style.display = 'none';
      }
      
      const element = reportContentRef.current;
      const opt = {
        margin: [15, 15, 15, 15] as [number, number, number, number],
        filename: `MAGPortal-Documentation-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
          backgroundColor: '#ffffff',
        },
        jsPDF: { 
          unit: 'mm' as const, 
          format: 'a4' as const, 
          orientation: 'portrait' as const,
          compress: true,
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] as any }
      };
      
      await html2pdf().set(opt).from(element).save();
      
      // Restore header visibility
      if (headerElement) {
        headerElement.style.display = originalDisplay;
      }
    } catch (error: any) {
      console.error('Failed to export PDF:', error);
      alert(`Failed to export PDF: ${error.message || 'Unknown error'}`);
      
      // Restore header visibility on error
      const headerElement = document.getElementById('doc-report-header');
      if (headerElement) {
        headerElement.style.display = '';
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div id="doc-report-header" className="mb-6 flex justify-between items-center sticky top-0 bg-white/80 backdrop-blur-sm p-4 rounded-lg shadow-sm z-10">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">MAGPortal</h1>
            <p className="text-sm text-slate-600 mt-1">Full Codebase Context Documentation</p>
          </div>
          <Button onClick={handleExportPDF} className="gap-2">
            <Download className="h-4 w-4" />
            Download PDF
          </Button>
        </div>

        {/* Main Content */}
        <div ref={reportContentRef} className="space-y-6">
          {/* Project Overview */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                <CardTitle>Project Overview</CardTitle>
              </div>
              <CardDescription>
                A production-ready authentication and authorization starter template
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700 leading-relaxed">
                MAGPortal is a full-stack TypeScript application designed as a comprehensive authentication and authorization starter template. It provides a solid foundation for building secure web applications with modern best practices.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="gap-1">
                  <Server className="h-3 w-3" />
                  Express.js + TypeScript
                </Badge>
                <Badge variant="secondary" className="gap-1">
                  <Code className="h-3 w-3" />
                  React 19
                </Badge>
                <Badge variant="secondary" className="gap-1">
                  <Database className="h-3 w-3" />
                  SQLite / PostgreSQL
                </Badge>
                <Badge variant="secondary" className="gap-1">
                  <Shield className="h-3 w-3" />
                  Dual Auth (Session + JWT)
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Core Architecture */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-primary" />
                <CardTitle>Core Architecture</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Monorepo Structure</h3>
                <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm">
                  <div className="text-slate-700">MAGPortal/</div>
                  <div className="text-slate-700 ml-4">├── Backend (TypeScript/Express) → compiles to dist/</div>
                  <div className="text-slate-700 ml-4">├── Frontend (React) → builds to dist/client/</div>
                  <div className="text-slate-700 ml-4">└── Single deployment artifact (dist/ folder)</div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Backend Structure</h3>
                <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm space-y-1">
                  <div className="text-slate-700">app.ts                    # Express app configuration</div>
                  <div className="text-slate-700 ml-4">├── auth/</div>
                  <div className="text-slate-700 ml-8">├── passport.ts       # Passport strategies (Local + JWT)</div>
                  <div className="text-slate-700 ml-8">├── jwt.ts           # Token generation/validation</div>
                  <div className="text-slate-700 ml-8">└── ttl.ts           # Time-to-live utilities</div>
                  <div className="text-slate-700 ml-4">├── routes/</div>
                  <div className="text-slate-700 ml-8">├── auth.api.ts      # JWT authentication endpoints</div>
                  <div className="text-slate-700 ml-8">├── auth.web.ts      # Session authentication endpoints</div>
                  <div className="text-slate-700 ml-8">└── admin.users.ts   # User management (admin only)</div>
                  <div className="text-slate-700 ml-4">├── models/index.ts       # Sequelize models</div>
                  <div className="text-slate-700 ml-4">├── middleware/</div>
                  <div className="text-slate-700 ml-8">├── rbac.ts          # Role/permission middleware</div>
                  <div className="text-slate-700 ml-8">└── validate.ts      # Zod validation middleware</div>
                  <div className="text-slate-700 ml-4">├── services/</div>
                  <div className="text-slate-700 ml-8">└── email.ts         # Email notifications</div>
                  <div className="text-slate-700 ml-4">└── db/</div>
                  <div className="text-slate-700 ml-8">├── sequelize.ts     # ORM configuration</div>
                  <div className="text-slate-700 ml-8">├── migrations/      # Schema migrations</div>
                  <div className="text-slate-700 ml-8">└── seeders/         # Initial data</div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Frontend Structure</h3>
                <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm space-y-1">
                  <div className="text-slate-700">client/src/</div>
                  <div className="text-slate-700 ml-4">├── App.tsx              # Main routing</div>
                  <div className="text-slate-700 ml-4">├── components/</div>
                  <div className="text-slate-700 ml-8">├── ui/              # shadcn/ui components</div>
                  <div className="text-slate-700 ml-8">├── customized/      # Extended components</div>
                  <div className="text-slate-700 ml-8">├── AppHeader.tsx    # Navigation</div>
                  <div className="text-slate-700 ml-8">└── ProtectedRoute.tsx  # Auth wrapper</div>
                  <div className="text-slate-700 ml-4">└── pages/</div>
                  <div className="text-slate-700 ml-8">├── Login.tsx</div>
                  <div className="text-slate-700 ml-8">├── ForgotPassword.tsx</div>
                  <div className="text-slate-700 ml-8">├── ResetPassword.tsx</div>
                  <div className="text-slate-700 ml-8">└── AdminUsers.tsx</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Database Schema */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                <CardTitle>Database Schema</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Models</h3>
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">1. User</h4>
                    <p className="text-sm text-slate-700 mb-2">Application users</p>
                    <ul className="text-sm text-slate-600 space-y-1 ml-4 list-disc">
                      <li>id (UUID), email, passwordHash, firstName, lastName, isActive, lastLoginAt</li>
                      <li>Hashed passwords (bcrypt, 12 rounds)</li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">2. Role</h4>
                    <p className="text-sm text-slate-700 mb-2">User roles (admin, user, etc.)</p>
                    <ul className="text-sm text-slate-600 space-y-1 ml-4 list-disc">
                      <li>id (UUID), name</li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">3. Permission</h4>
                    <p className="text-sm text-slate-700 mb-2">Fine-grained permissions</p>
                    <ul className="text-sm text-slate-600 space-y-1 ml-4 list-disc">
                      <li>id (UUID), name</li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">4. RefreshToken</h4>
                    <p className="text-sm text-slate-700 mb-2">JWT refresh tokens</p>
                    <ul className="text-sm text-slate-600 space-y-1 ml-4 list-disc">
                      <li>id (UUID), userId, tokenHash, jti, revokedAt, expiresAt</li>
                      <li>Implements token rotation for security</li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">5. PasswordResetToken</h4>
                    <p className="text-sm text-slate-700 mb-2">Password reset tokens</p>
                    <ul className="text-sm text-slate-600 space-y-1 ml-4 list-disc">
                      <li>id (UUID), userId, tokenHash, expiresAt, usedAt</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Relationships</h3>
                <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm">
                  <div className="text-slate-700">User ←→ UserRoles ←→ Role ←→ RolePermissions ←→ Permission</div>
                  <div className="text-slate-700 mt-2">User → RefreshToken (one-to-many)</div>
                  <div className="text-slate-700">User → PasswordResetToken (one-to-many)</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Authentication System */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Key className="h-5 w-5 text-primary" />
                <CardTitle>Authentication System</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Dual Authentication Approach</h3>
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">1. Session-based (`/auth/*` routes)</h4>
                    <ul className="text-sm text-slate-700 space-y-1 ml-4 list-disc">
                      <li>Uses Passport Local strategy</li>
                      <li>Session stored in database (connect-session-sequelize)</li>
                      <li>Good for traditional web apps</li>
                      <li>CSRF protection via SameSite cookies</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">2. JWT-based (`/api/auth/*` routes)</h4>
                    <ul className="text-sm text-slate-700 space-y-1 ml-4 list-disc">
                      <li>Uses Passport JWT strategy</li>
                      <li>Access tokens (15 min) + Refresh tokens (7 days)</li>
                      <li>Refresh token rotation on every use</li>
                      <li>Stateless, mobile-friendly</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Token Security</h3>
                <ul className="text-sm text-slate-700 space-y-2 ml-4 list-disc">
                  <li>Access tokens: Short-lived (15 minutes)</li>
                  <li>Refresh tokens: Long-lived (7 days), stored hashed in database</li>
                  <li>JTI (JWT ID) for tracking</li>
                  <li>Automatic rotation prevents stolen token reuse</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Authorization (RBAC) */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <CardTitle>Authorization (RBAC)</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Three-tier Permission Model</h3>
                <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm text-center">
                  <div className="text-slate-700">Users → Roles → Permissions</div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Middleware Usage</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 mb-2">Require specific role:</p>
                    <pre className="bg-slate-900 text-slate-100 p-3 rounded text-xs overflow-x-auto">
{`router.get('/admin/users',
  passport.authenticate('jwt', { session: false }),
  requireRole(['admin']),
  controller
);`}
                    </pre>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 mb-2">Require specific permission:</p>
                    <pre className="bg-slate-900 text-slate-100 p-3 rounded text-xs overflow-x-auto">
{`router.post('/resource',
  passport.authenticate('jwt', { session: false }),
  requirePermission(['resource:write']),
  controller
);`}
                    </pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* API Endpoints */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Server className="h-5 w-5 text-primary" />
                <CardTitle>API Endpoints</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Authentication</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="font-mono text-xs">POST</Badge>
                    <code className="text-sm text-slate-700">/api/auth/login</code>
                    <span className="text-sm text-slate-600">- Login, returns JWT tokens</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="font-mono text-xs">POST</Badge>
                    <code className="text-sm text-slate-700">/api/auth/refresh</code>
                    <span className="text-sm text-slate-600">- Refresh access token</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="font-mono text-xs">GET</Badge>
                    <code className="text-sm text-slate-700">/api/auth/me</code>
                    <span className="text-sm text-slate-600">- Get current user (requires auth)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="font-mono text-xs">POST</Badge>
                    <code className="text-sm text-slate-700">/api/auth/request-reset</code>
                    <span className="text-sm text-slate-600">- Request password reset</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="font-mono text-xs">POST</Badge>
                    <code className="text-sm text-slate-700">/api/auth/reset</code>
                    <span className="text-sm text-slate-600">- Reset password with token</span>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Admin (requires admin role)</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="font-mono text-xs">GET</Badge>
                    <code className="text-sm text-slate-700">/api/admin/users</code>
                    <span className="text-sm text-slate-600">- List users (paginated, searchable)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="font-mono text-xs">POST</Badge>
                    <code className="text-sm text-slate-700">/api/admin/users</code>
                    <span className="text-sm text-slate-600">- Create user</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="font-mono text-xs">PATCH</Badge>
                    <code className="text-sm text-slate-700">/api/admin/users/:id/status</code>
                    <span className="text-sm text-slate-600">- Activate/deactivate user</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="font-mono text-xs">DELETE</Badge>
                    <code className="text-sm text-slate-700">/api/admin/users/:id</code>
                    <span className="text-sm text-slate-600">- Delete user</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className="font-mono text-xs">POST</Badge>
                    <code className="text-sm text-slate-700">/api/admin/users/:id/reset</code>
                    <span className="text-sm text-slate-600">- Trigger password reset</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Frontend Patterns */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Code className="h-5 w-5 text-primary" />
                <CardTitle>Frontend Patterns</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Component Library</h3>
                <p className="text-sm text-slate-700 mb-3">
                  Uses shadcn/ui (copy-paste components, not npm package):
                </p>
                <ul className="text-sm text-slate-700 space-y-1 ml-4 list-disc">
                  <li>Components in <code className="bg-slate-100 px-1 rounded">client/src/components/ui/</code></li>
                  <li>Customizable, owned by codebase</li>
                  <li>Built on Radix UI primitives</li>
                  <li>Styled with Tailwind CSS</li>
                </ul>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Protected Routes</h3>
                <pre className="bg-slate-900 text-slate-100 p-3 rounded text-xs overflow-x-auto">
{`<Route path="/admin/users" element={
  <ProtectedRoute roles={["admin"]}>
    <AdminUsers />
  </ProtectedRoute>
} />`}
                </pre>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">State Management</h3>
                <ul className="text-sm text-slate-700 space-y-1 ml-4 list-disc">
                  <li>No global state library (React hooks)</li>
                  <li>JWT tokens stored in memory (not localStorage for security)</li>
                  <li>Session-based auth uses server-side sessions</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Development Workflow */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                <CardTitle>Development Workflow</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Commands</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <code className="text-sm text-slate-700 font-mono">npm run dev</code>
                    <span className="text-sm text-slate-600">- Run both backend + frontend with hot reload</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <code className="text-sm text-slate-700 font-mono">npm run build:all</code>
                    <span className="text-sm text-slate-600">- Build both for production</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <code className="text-sm text-slate-700 font-mono">npm run db:migrate</code>
                    <span className="text-sm text-slate-600">- Run database migrations</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <code className="text-sm text-slate-700 font-mono">npm run db:seed</code>
                    <span className="text-sm text-slate-600">- Seed initial data</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <code className="text-sm text-slate-700 font-mono">npm run db:reset</code>
                    <span className="text-sm text-slate-600">- Reset database</span>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Development Mode</h3>
                <ul className="text-sm text-slate-700 space-y-1 ml-4 list-disc">
                  <li>Backend: nodemon watches `.ts` files, auto-restarts</li>
                  <li>Frontend: chokidar watches React files, rebuilds to `dist/client/`</li>
                  <li>Single server on port 3000 serves both</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Security Features */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <CardTitle>Security Features</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Implemented</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Bcrypt password hashing (12 rounds)
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Helmet.js security headers
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Rate limiting on auth endpoints
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    CORS configuration
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    JWT token expiration
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Refresh token rotation
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    HTTP-only cookies for sessions
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Password reset token hashing
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Input validation (Zod schemas)
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Configuration (Environment Variables)</h3>
                <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm space-y-1">
                  <div className="text-slate-700">SESSION_SECRET          # Session encryption</div>
                  <div className="text-slate-700">JWT_SECRET             # JWT signing</div>
                  <div className="text-slate-700">DATABASE_URL           # Database connection</div>
                  <div className="text-slate-700">SMTP_*                 # Email configuration</div>
                  <div className="text-slate-700">CLIENT_ORIGINS         # CORS allowed origins</div>
                  <div className="text-slate-700">RATE_LIMIT_*          # Rate limiting settings</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Key Technologies */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                <CardTitle>Key Technologies</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Backend Dependencies</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">express</span>
                    <span className="text-slate-600">4.16.1</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">sequelize</span>
                    <span className="text-slate-600">6.37.7</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">passport</span>
                    <span className="text-slate-600">0.7.0</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">bcrypt</span>
                    <span className="text-slate-600">6.0.0</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">jsonwebtoken</span>
                    <span className="text-slate-600">9.0.2</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">zod</span>
                    <span className="text-slate-600">4.1.12</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">helmet</span>
                    <span className="text-slate-600">8.1.0</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">nodemailer</span>
                    <span className="text-slate-600">7.0.10</span>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Frontend Dependencies</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">react</span>
                    <span className="text-slate-600">19.2.0</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">react-router-dom</span>
                    <span className="text-slate-600">7.9.5</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">tailwindcss</span>
                    <span className="text-slate-600">3.4.18</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">@radix-ui/*</span>
                    <span className="text-slate-600">UI primitives</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">@tanstack/react-table</span>
                    <span className="text-slate-600">8.21.3</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current State */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle>Current State</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">What's Working ✓</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Full authentication system (session + JWT)
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Complete RBAC with roles and permissions
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    User management admin interface
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Password reset flow with email
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Protected routes (frontend + backend)
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Database migrations and seeders
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Production-ready security measures
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    Responsive UI with shadcn/ui
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Not Yet Implemented</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                    Two-factor authentication
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                    OAuth/social login
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                    User profile editing
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                    Email verification on signup
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                    Comprehensive test suite
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                    Docker configuration
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                    API documentation (Swagger)
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                    Audit logging
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Design Patterns */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-primary" />
                <CardTitle>Design Patterns Used</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm flex-shrink-0 mt-0.5">1</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Middleware Chaining</h4>
                    <p className="text-sm text-slate-600">Authentication → Authorization → Validation → Controller</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm flex-shrink-0 mt-0.5">2</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Service Layer</h4>
                    <p className="text-sm text-slate-600">Business logic extracted (email service, JWT service)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm flex-shrink-0 mt-0.5">3</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Repository Pattern</h4>
                    <p className="text-sm text-slate-600">Sequelize models as data access layer</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm flex-shrink-0 mt-0.5">4</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Protected Routes</h4>
                    <p className="text-sm text-slate-600">HOC pattern for route guards</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm flex-shrink-0 mt-0.5">5</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Environment-based Config</h4>
                    <p className="text-sm text-slate-600">All sensitive values in env vars</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Build Process */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                <CardTitle>Build Process</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Development</h3>
                <ol className="text-sm text-slate-700 space-y-2 ml-4 list-decimal">
                  <li>Frontend watches for changes, rebuilds to <code className="bg-slate-100 px-1 rounded">dist/client/</code></li>
                  <li>Backend watches TypeScript files, auto-restarts</li>
                  <li>Single server serves both</li>
                </ol>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Production</h3>
                <ol className="text-sm text-slate-700 space-y-2 ml-4 list-decimal">
                  <li><code className="bg-slate-100 px-1 rounded">npm run build:client</code> - React → <code className="bg-slate-100 px-1 rounded">dist/client/</code></li>
                  <li><code className="bg-slate-100 px-1 rounded">npm run build</code> - TypeScript → <code className="bg-slate-100 px-1 rounded">dist/</code></li>
                  <li><code className="bg-slate-100 px-1 rounded">npm start</code> - Run from <code className="bg-slate-100 px-1 rounded">dist/</code></li>
                  <li>Backend serves frontend statically</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* How to Use This Context */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle>How to Use This Context</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700 mb-4">
                I'm looking to extend this application. When I ask questions or request features, please:
              </p>
              <ol className="text-sm text-slate-700 space-y-2 ml-4 list-decimal">
                <li><strong>Understand the existing patterns</strong> - Don't suggest approaches that conflict with the established architecture</li>
                <li><strong>Respect the tech stack</strong> - Solutions should use the existing technologies</li>
                <li><strong>Follow security practices</strong> - Maintain the security standards already in place</li>
                <li><strong>Use existing components</strong> - Leverage shadcn/ui and existing patterns</li>
                <li><strong>Consider both frontend and backend</strong> - Features often need changes in both</li>
                <li><strong>Maintain type safety</strong> - All code should be properly typed with TypeScript</li>
                <li><strong>Follow the middleware pattern</strong> - Use passport, RBAC middleware, and validation</li>
                <li><strong>Respect the dual auth system</strong> - Understand when to use sessions vs JWT</li>
              </ol>
            </CardContent>
          </Card>

          {/* What I Need Help With */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <CardTitle>What I Need Help With</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700 mb-4">
                When I describe a feature or ask a question, provide:
              </p>
              <ul className="text-sm text-slate-700 space-y-2 ml-4 list-disc">
                <li>Specific file paths where changes should be made</li>
                <li>Code that follows the existing patterns and style</li>
                <li>Consideration of both frontend and backend changes</li>
                <li>Database migration needs (if schema changes required)</li>
                <li>Security implications</li>
                <li>Integration with existing auth/RBAC system</li>
              </ul>
            </CardContent>
          </Card>

          {/* Example Use Cases */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle>Example Use Cases</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700 mb-4">
                This context should help you understand requests like:
              </p>
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="text-sm text-slate-700">• "Add a user profile page where users can edit their information"</div>
                <div className="text-sm text-slate-700">• "Implement a dashboard with user analytics"</div>
                <div className="text-sm text-slate-700">• "Add social login with Google OAuth"</div>
                <div className="text-sm text-slate-700">• "Create an API endpoint for uploading user avatars"</div>
                <div className="text-sm text-slate-700">• "Add two-factor authentication"</div>
                <div className="text-sm text-slate-700">• "Implement role-based UI showing/hiding based on permissions"</div>
                <div className="text-sm text-slate-700">• "Add email verification during signup"</div>
                <div className="text-sm text-slate-700">• "Create a settings page for admin configuration"</div>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="pt-6">
              <p className="text-center text-slate-700 text-sm">
                Do you understand the MAGPortal codebase and are you ready to help me build features on top of this foundation?
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
